import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LoadingBarService } from '@ngx-loading-bar/core';
import Swal from 'sweetalert2';
import { CanComponentDeactivate } from '@core/interfaces/can-deactivate.interface';
import { AppPermissionService } from '@core/services/app-permission.service';
import { GroupHttpService } from '@core/services/group-http.service';
import { UploadHttpService } from '@core/services/upload-http.service';
import {
  DAILY_CHECKIN_DAY_COUNT,
  DailyCheckInCurrency,
  DailyCheckInRewardType,
  DailyCheckInSchedule,
  DailyCheckInSet,
  DailyCheckInSettings,
} from '@core/models/daily-check-in.model';
import { DailyCheckInDataService } from './services/daily-check-in-data.service';

/**
 * BO Daily Check-In configuration — AIO-80387.
 *
 * Two panels, each with its own Save:
 *  - Basic settings: feature toggle, missed-day reset, floating icon.
 *  - Reward schedule: a grid of sets x 7 days, configured per currency.
 *
 * HTTP errors are NOT handled here on purpose. ApiErrorInterceptor (app.module.ts) wraps every
 * request and raises a "System Message" dialog for timeouts and all 4xx/5xx except 403 (which
 * logs the operator out) and 409 (routed to onDetectIdenticalSettings; no endpoint here returns
 * it). None of this page's calls set Skip-Error-Interceptor, so every failure is already shown.
 * The error callbacks below therefore only clear their spinner — adding a Swal would raise a
 * second dialog for the same failure. The one case the interceptor cannot see is an HTTP 200
 * carrying an empty body, which is why upload() handles that itself.
 *
 * A day-cell is per (day, currency): Day 1 can be a credit in MYR and a prize in THB. Every
 * currency's cells are held in memory at once, so switching the currency selector rebinds the
 * grid without discarding unsaved edits in another currency, and a save sends the whole
 * cross-currency grid. Saving PUTs one request per dirty set, so a rejected set fails alone.
 */
@Component({
  selector: 'kt-daily-check-in',
  templateUrl: './daily-check-in.component.html',
  styleUrls: ['./daily-check-in.component.scss'],
})
export class DailyCheckInComponent implements OnInit, OnDestroy, CanComponentDeactivate {

  /** Keeps the problem list readable when a whole currency is still blank. */
  private static readonly MAX_LISTED_PROBLEMS = 10;

  readonly dayCount = DAILY_CHECKIN_DAY_COUNT;
  readonly days = Array.from({ length: DAILY_CHECKIN_DAY_COUNT }, (_, i) => i + 1);
  readonly rewardType = DailyCheckInRewardType;
  readonly rewardTypes = [
    { id: DailyCheckInRewardType.None, name: 'No Reward' },
    { id: DailyCheckInRewardType.Credit, name: 'Credit' },
    { id: DailyCheckInRewardType.Promo, name: 'Promo' },
    { id: DailyCheckInRewardType.Prize, name: 'Prize' },
  ];

  /**
   * Success messages from the API, rendered by <kt-swal-alert> in the template. The data
   * service taps every write response's `message` array into this Subject.
   */
  messages$ = this.dataService.messages$;

  settingsForm: FormGroup;

  /** Working copy of the grid. Not an ngrx entity snapshot — the grid is edited in place. */
  sets: DailyCheckInSet[] = [];

  /**
   * Unsaved tracking, same idea as partner-page: hold the last-saved payload per set and call
   * a set dirty when its current payload differs, so undoing an edit clears the flag instead
   * of leaving it stuck until save. Partner-page recomputes inside a getter on every change
   * detection pass; this grid holds 7 x currencies cells per set, so the comparison runs on
   * edit and the template reads a cheap Set lookup.
   *
   * A set with no id has never reached the API and counts as pending regardless — the same
   * rule as partner-page's `section.id === null`.
   */
  private baselines = new WeakMap<DailyCheckInSet, string>();
  private dirtySets = new Set<DailyCheckInSet>();

  /** Last-saved basic-settings values, for the same comparison on that panel. */
  private initSettingsValue = '';

  currencies: DailyCheckInCurrency[] = [];
  /**
   * The subset of `currencies` this operator may edit. The grid still HOLDS every currency (see
   * loadReferenceData) — this only drives the selector, so an operator with MYR access cannot
   * open THB, and a save still carries THB's existing cells through untouched.
   */
  editableCurrencies: DailyCheckInCurrency[] = [];
  selectedCurrencyId: number = null;
  memberGroups: any[] = [];
  promotions: any[] = [];

  /**
   * Groups not yet in any set — those members see no check-in at all.
   *
   * A field, not a getter: angular2-multiselect holds its own state (search term, filtered
   * list) and re-initialises whenever [data] changes identity. A getter hands it a fresh array
   * on every change-detection pass, which would wipe whatever the operator is typing.
   */
  unassignedGroups: any[] = [];

  /**
   * Same widget and settings shape as the Member Group filter on 3.2.1 Promotion Codes
   * (angular2-multiselect): multi-pick, search box, "select all filtered results".
   */
  addGroupDropdownSettings = {
    singleSelection: false,
    text: '+ Add group',
    labelKey: 'name',
    primaryKey: 'id',
    enableSearchFilter: true,
    enableFilterSelectAll: true,
    enableCheckAll: false,
    showCheckbox: true,
    searchPlaceholderText: 'Search',
    classes: 'dci-addgroup-dd',
    maxHeight: 190,
    // Always downward. autoPosition (on by default) flips the panel upward when the VIEWPORT
    // has more room above — but upward is where .dci-scroll clips, and the padding trick only
    // opens room below. Pinning the direction is what keeps the list visible.
    position: 'bottom',
    autoPosition: false,
  };

  /** Single-pick sibling of the group picker, for the promo cells. */
  promotionDropdownSettings = {
    singleSelection: true,
    text: 'Select promotion',
    labelKey: 'name',
    primaryKey: 'id',
    enableSearchFilter: true,
    showCheckbox: false,
    searchPlaceholderText: 'Search',
    classes: 'dci-promo-dd',
    maxHeight: 190,
    position: 'bottom',
    autoPosition: false,
    // Explicit false, not omitted. On a settings change the widget does
    // `Object.assign(this.defaultSettings, this.settings)` — a merge, not a replace — so a key
    // this object leaves out keeps whatever the disabled object last set. Without this the
    // picker stays dead after its first disabled render, even though the label updates.
    disabled: false,
  };

  /**
   * The same settings with the picker switched off, for a set that has no member group yet.
   * A separate object rather than a mutated one: the widget re-initialises when [settings]
   * changes identity, and both fields are stable references.
   */
  promotionDropdownDisabledSettings = {
    singleSelection: true,
    text: 'Add a member group first',
    labelKey: 'name',
    primaryKey: 'id',
    enableSearchFilter: true,
    showCheckbox: false,
    searchPlaceholderText: 'Search',
    classes: 'dci-promo-dd',
    maxHeight: 190,
    position: 'bottom',
    autoPosition: false,
    disabled: true,
  };

  /**
   * Switched off because the operator may not edit this set, not because it lacks a group — so
   * the placeholder stays neutral rather than naming a fix they cannot apply. A third stable
   * reference for the same reason as the second: the widget re-initialises on identity change.
   */
  promotionDropdownReadonlySettings = {
    singleSelection: true,
    text: 'Select promotion',
    labelKey: 'name',
    primaryKey: 'id',
    enableSearchFilter: true,
    showCheckbox: false,
    searchPlaceholderText: 'Search',
    classes: 'dci-promo-dd',
    maxHeight: 190,
    position: 'bottom',
    autoPosition: false,
    disabled: true,
  };

  /** Widens the grid's clip box while a panel is open — see .dci-scroll--room. */
  groupDropdownOpen = false;

  /**
   * Rows the operator just created, keyed to the currency they were created in. A brand-new
   * set carries no configuration yet, so without this it would vanish from the grid the moment
   * it was added. The pin is dropped once the row is cleared, deleted or reloaded.
   */
  private pinnedSets = new Map<DailyCheckInSet, number>();

  /**
   * The set whose group picker is open. Every .dci-setcol is position: sticky with z-index 2,
   * which makes each cell its own stacking context — so a panel inside one cell cannot paint
   * over the next row's cell no matter how high its own z-index is. The fix is to raise the
   * containing CELL while its panel is open.
   */
  openGroupDropdownSet: DailyCheckInSet = null;

  loading = false;
  settingsLoading = false;
  scheduleLoading = false;

  /**
   * Scoped to the floating-icon box so a cell-image upload elsewhere in the grid does not put
   * a spinner in it — `loading` is raised by every upload on the page.
   */
  iconUploading = false;

  /** Same scoping as iconUploading, for the optional missed-day icon box. */
  missedIconUploading = false;

  /** Missed Day Rule options — values are DailyCheckInSetting::RESET_* on the API. */
  readonly resetModes = [
    { value: 1, label: 'Reset on Miss' },
    { value: 0, label: 'Continue Progress' },
    { value: 2, label: 'Daily Only' },
  ];

  /** Enlarged floating icon, same click-to-preview the 19.1 Partner Page box has. */
  showIconPreview = false;

  /** Host for that overlay — relocated to <body> in ngOnInit, see the template for why. */
  @ViewChild('iconPreviewHost', { static: true }) iconPreviewHost: ElementRef<HTMLElement>;

  // permissions
  canViewDailyCheckIn: boolean;
  canEditDailyCheckInSettings: boolean;
  canCreateDailyCheckInSet: boolean;
  canEditDailyCheckInSet: boolean;
  canDeleteDailyCheckInSet: boolean;

  private subscriptions = new Subscription();

  constructor(
    private dataService: DailyCheckInDataService,
    private groupHttpService: GroupHttpService,
    private uploadHttpService: UploadHttpService,
    private appPermissionService: AppPermissionService,
    private loadingBar: LoadingBarService,
    private translateService: TranslateService,
  ) { }

  ngOnInit() {
    this.formInit();
    this.loadReferenceData();

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canViewDailyCheckIn = appPermissions.view_daily_checkin_list;
      this.canEditDailyCheckInSettings = appPermissions.edit_daily_checkin_settings;
      this.canCreateDailyCheckInSet = appPermissions.create_daily_checkin_set;
      this.canEditDailyCheckInSet = appPermissions.edit_daily_checkin_set;
      this.canDeleteDailyCheckInSet = appPermissions.delete_daily_checkin_set;
    });

    this.subscriptions.add(apSub);

    // The *ngIf inside it anchors to a comment node that travels with the host, so Angular
    // still owns the overlay and adds/removes it normally once it lives on <body>.
    document.body.appendChild(this.iconPreviewHost.nativeElement);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();

    // Angular only tears down nodes it can still find under this view — the relocated host is
    // no longer one of them, so it would outlive the page and stack up on every visit.
    const host = this.iconPreviewHost.nativeElement;

    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
  }

  /**
   * Route guard — wired via UnSaveChangesGuard on the route, the same way partner-page does it.
   * Without this a half-built grid (up to 7 x currencies cells per set) is discarded silently
   * on navigation.
   */
  async canDeactivate(): Promise<boolean> {
    if (!this.hasUnsavedChanges()) {
      return true;
    }

    return Swal.fire({
      title: '<div class="text-center">' + this.translateService.instant('Unsaved changes') + '</div>',
      html: this.buildUnsavedHtml(),
      showDenyButton: true,
      showCloseButton: true,
      showCancelButton: false,
      showConfirmButton: true,
      reverseButtons: true,
      denyButtonText: this.translateService.instant('Cancel'),
      confirmButtonText: this.translateService.instant('Discard'),
      icon: 'warning',
      customClass: {
        denyButton: 'deny-button',
        confirmButton: 'confirm-button',
      }
    }).then(res => res.isConfirmed);
  }

  /**
   * Native prompt on refresh / tab-close / URL change — the route guard can't intercept those.
   * The wording is the browser's and can't be customised.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  hasUnsavedChanges(): boolean {
    return this.settingsDirty || this.hasUnsavedSets;
  }

  /** Names the unsaved panels so the leave prompt is clear at a glance. */
  private buildUnsavedHtml(): string {
    const items: string[] = [];

    if (this.settingsDirty) {
      items.push(this.translateService.instant('Basic settings'));
    }

    if (this.hasUnsavedSets) {
      items.push(this.translateService.instant('Reward schedule'));
    }

    // The dot is inlined rather than a <ul> marker: SweetAlert renders outside this component
    // and the KT theme strips default list bullets.
    const rows = items
      .map(item => '<div style="margin-bottom: 4px;"><span style="color: #48465b; margin-right: 8px;">&bull;</span>'
        + this.escapeHtml(item) + '</div>')
      .join('');

    return '<div class="text-left" style="padding-left: 0.5rem;">'
      + '<p class="mb-2">' + this.translateService.instant('These changes are not saved yet:') + '</p>'
      + rows
      + '</div>';
  }

  private formInit() {
    this.settingsForm = new FormGroup({
      status: new FormControl(0),
      reset_on_missed: new FormControl(1),
      icon_path: new FormControl(null),
      missed_icon_path: new FormControl(null),
    });
  }

  /**
   * Currencies come from the API, never a hardcoded list — the mockup shows VND, which is not
   * a configured currency in this environment.
   */
  private loadReferenceData() {
    this.loading = true;
    this.loadingBar.start();

    const sub = forkJoin({
      memberGroups: this.groupHttpService.getGroups(),
      promotions: this.dataService.getPromotions(),
      settings: this.dataService.getSettings(),
      sets: this.dataService.getAll(),
    }).subscribe(res => {
      this.currencies = res.settings.currencies || [];
      this.memberGroups = res.memberGroups || [];
      this.promotions = res.promotions || [];
      // `currencies` stays the FULL configured list — hydrateSet and toPayload both walk it, and
      // the API replaces a set's cells wholesale, so dropping a currency here would delete its
      // schedules on the next save. Only what the selector offers is narrowed.
      this.editableCurrencies = this.operatorCurrencies();
      this.selectedCurrencyId = this.editableCurrencies.length ? this.editableCurrencies[0].id : null;

      this.patchSettings(res.settings.settings);

      this.dirtySets.clear();
      this.pinnedSets.clear();
      this.sets = (res.sets || []).map(set => this.hydrateSet(set));
      this.sets.forEach(set => this.snapshot(set));
      this.sets.forEach(set => this.flagPromotionEligibility(set));
      this.refreshUnassignedGroups();

      this.loading = false;
      this.loadingBar.complete();
    }, () => {
      // Spinner only — ApiErrorInterceptor already showed the dialog. See class header.
      this.loading = false;
      this.loadingBar.complete();
    });

    this.subscriptions.add(sub);
  }

  private patchSettings(settings: DailyCheckInSettings) {
    if (settings) {
      this.settingsForm.patchValue({
        status: Number(settings.status),
        reset_on_missed: Number(settings.reset_on_missed),
        icon_path: settings.icon_path,
        missed_icon_path: settings.missed_icon_path ?? null,
      });
    }

    // Baseline is taken even when the API returned nothing, so an untouched form never reads
    // as dirty against an empty string.
    this.initSettingsValue = JSON.stringify(this.settingsForm.value);
  }

  /**
   * The currencies this BO user is assigned, intersected with the ones check-in is configured
   * for. Read from `user_data.bo_currency_ids` — the same source deposits, withdrawals, the
   * affiliate queues and the notification topbar all scope themselves by.
   *
   * Matched on ID: bo_currency_ids holds settings_currency ids (AuthData carries it beside
   * settings_currency_id, and the /currency dropdown's id feeds settings_currency_id on 17.x),
   * which is exactly what DailyCheckInCurrency.id and every schedule row use.
   *
   * NOT sessionStorage.currencies — that key is only written by the login effect, so it is
   * missing for anyone who did not sign in this session and the page would silently unscope.
   *
   * Falls back to the full list when the key is absent or unreadable — a corrupted user_data
   * should not lock an operator out of a page the server will still let them save. The real
   * gate is server-side; this only keeps the UI honest.
   */
  private operatorCurrencies(): DailyCheckInCurrency[] {
    let assigned: number[];

    try {
      // Guarded: a corrupted user_data throws here rather than breaking the whole page.
      assigned = (JSON.parse(localStorage.getItem('user_data')) || {}).bo_currency_ids;
    } catch {
      assigned = null;
    }

    if (!assigned || !assigned.length) {
      return this.currencies;
    }

    const ids = assigned.map(id => Number(id));

    return this.currencies.filter(currency => ids.indexOf(Number(currency.id)) > -1);
  }

  /**
   * Build the full day x currency matrix. The API returns only the cells that exist, so any
   * currency added since the set was last saved is filled in blank here rather than left
   * missing — the grid must be able to bind every column.
   */
  private hydrateSet(set: DailyCheckInSet): DailyCheckInSet {
    const schedules: DailyCheckInSchedule[] = [];

    this.currencies.forEach(currency => {
      this.days.forEach(day => {
        const existing = (set.schedules || []).find(
          s => Number(s.day) === day && Number(s.settings_currency_id) === Number(currency.id)
        );

        schedules.push(existing
          ? {
              ...existing,
              day,
              settings_currency_id: Number(currency.id),
              reward_type: Number(existing.reward_type),
              amount: this.toDisplayAmount(this.trimAmount(existing.amount), Number(currency.id)),
              promotion_selection: this.promotionSelectionFor(existing.promotion_id),
            }
          : this.blankSchedule(day, Number(currency.id)));
      });
    });

    return {
      ...set,
      status: Number(set.status),
      member_group_ids_by_currency: this.groupsByCurrency(set.member_groups || []),
      schedules,
    };
  }

  /**
   * The API stores amounts as decimal(30,8), so it hands back "10.00000000". Trailing zeros
   * past the cents are noise in a number input — trim them without touching the value.
   */
  /**
   * How many of a currency's smallest units one box-unit stands for.
   *
   * IDR is entered in thousands against a fixed ".000" suffix — the operator types 10 and means
   * Rp 10.000 — which is how the player deposit form already works. `ratio` is the house field
   * for exactly this (Deposit.php divides by it before handing an amount to a gateway), so it is
   * reused rather than a second table of magic numbers. Every other currency has ratio 1.
   *
   * The DATABASE always holds the real amount. Only the box is scaled: storing 10 for IDR would
   * make the claim flow pay Rp 10 instead of Rp 10.000.
   */
  private amountScale(settingsCurrencyId: number): number {
    const currency = this.currencies.find(c => Number(c.id) === Number(settingsCurrencyId));
    const ratio = currency ? Number(currency.ratio) : 1;

    return ratio > 1 ? ratio : 1;
  }

  /** Real stored amount -> what the operator sees in the box. */
  private toDisplayAmount(stored: string, settingsCurrencyId: number): string {
    const scale = this.amountScale(settingsCurrencyId);

    if (scale === 1 || !this.hasValue(stored) || isNaN(Number(stored))) {
      return stored;
    }

    return `${Number(stored) / scale}`;
  }

  /** What the operator typed -> the real amount to store. */
  private toStoredAmount(display: string, settingsCurrencyId: number): string {
    const scale = this.amountScale(settingsCurrencyId);

    if (scale === 1 || !this.hasValue(display) || isNaN(Number(display))) {
      return display;
    }

    // Integer math on purpose. A scaled box only ever holds whole units (creditAmountProblem
    // rejects anything else), so this stays exact — `0.1 * 1000` in floating point is
    // 100.00000000000001, and that must never reach a money column.
    return `${Math.round(Number(display) * scale)}`;
  }

  private trimAmount(amount: string | null): string {
    if (amount === null || amount === undefined || `${amount}`.trim() === '') {
      return '';
    }

    const asNumber = Number(amount);

    return isNaN(asNumber) ? `${amount}` : `${asNumber}`;
  }

  private blankSchedule(day: number, settingsCurrencyId: number): DailyCheckInSchedule {
    return {
      day,
      settings_currency_id: settingsCurrencyId,
      // No Reward, not null: "not set" is gone from the dropdown, so every cell starts as a
      // valid day that simply pays nothing. isCurrencyConfigured() is what still tells an
      // untouched currency apart from one deliberately left as No Reward.
      reward_type: DailyCheckInRewardType.None,
      amount: '',
      promotion_id: null,
      prize_name: null,
      image_path: null,
      promotion_selection: [],
    };
  }

  /**
   * The picker binds to item objects, so a saved promotion_id has to be resolved back to one.
   * A promo that has since been deactivated is not in the list — keep the id showing so the
   * cell still reports itself as configured and promotion_available can flag it.
   */
  private promotionSelectionFor(promotionId: number | null): any[] {
    if (promotionId === null || promotionId === undefined) {
      return [];
    }

    const promotion = this.promotions.find(p => Number(p.id) === Number(promotionId));

    return [promotion || { id: promotionId, name: `#${promotionId}` }];
  }

  // ----- grid helpers ---------------------------------------------------------------------

  get selectedCurrency(): DailyCheckInCurrency {
    return this.currencies.find(c => Number(c.id) === Number(this.selectedCurrencyId));
  }

  /**
   * Placeholder on the credit box — the real floor for this currency, which is not a fixed
   * 0.10: IDR's is 1000. Comes from the API so CheckInCreditAmountRange stays the only place
   * the rule is defined.
   */
  get amountPlaceholder(): string {
    const currency = this.selectedCurrency;

    if (!currency || !currency.min_amount) {
      return '';
    }

    return `${this.translateService.instant('min')} `
      + `${this.toDisplayAmount(currency.min_amount, Number(currency.id))}`;
  }

  /** Symbol shown as the prefix on credit inputs (RM, S$, ฿ ...). */
  get currencySymbol(): string {
    const currency = this.selectedCurrency;
    return currency ? currency.symbol : '';
  }

  /**
   * The fixed tail shown after a scaled amount box — ".000" for IDR, nothing for everyone else.
   * Derived from the ratio's own width so it stays right if another currency is ever scaled.
   */
  get amountScaleSuffix(): string {
    const scale = this.amountScale(Number(this.selectedCurrencyId));

    return scale > 1 ? `.${`${scale}`.slice(1)}` : '';
  }

  /**
   * Day 7 closes the cycle and is marked in the header, matching the design. Purely a sign —
   * it says nothing about what that day pays, which is per set and per currency anyway.
   */
  isFinalDay(day: number): boolean {
    return day === this.dayCount;
  }

  /**
   * Sets shown for the currency being edited: those configured in it, plus any row just added
   * or duplicated here. A set configured only in SGD is simply not part of MYR's grid — adding,
   * duplicating and clearing all stay within one currency.
   *
   * Saving still walks `sets`, never this list, so a currency hidden from view is never lost.
   */
  /**
   * Every edit in the schedule panel is scoped to the currency on screen, so with no assignable
   * currency there is nothing to edit — addSet() would pin a row to currency 0 and strand it.
   */
  get hasEditableCurrency(): boolean {
    return !!this.selectedCurrencyId;
  }

  get visibleSets(): DailyCheckInSet[] {
    return this.sets.filter(set =>
      this.isCurrencyConfigured(set, this.selectedCurrencyId)
      || this.pinnedSets.get(set) === Number(this.selectedCurrencyId));
  }

  /** Groups the API returned, folded into one list per currency. */
  private groupsByCurrency(rows: any[]): { [currencyId: number]: number[] } {
    const map: { [currencyId: number]: number[] } = {};

    this.currencies.forEach(currency => { map[Number(currency.id)] = []; });

    rows.forEach(row => {
      const currencyId = Number(row.settings_currency_id);

      if (!map[currencyId]) {
        map[currencyId] = [];
      }

      map[currencyId].push(Number(row.member_group_id));
    });

    return map;
  }

  /**
   * The groups this set holds in the currency being edited. Returns the live array so callers
   * can push/filter it, creating the slot on demand for a currency added since the last save.
   */
  groupIdsFor(set: DailyCheckInSet, settingsCurrencyId: number = null): number[] {
    const currencyId = Number(settingsCurrencyId === null ? this.selectedCurrencyId : settingsCurrencyId);

    if (!set.member_group_ids_by_currency) {
      set.member_group_ids_by_currency = {};
    }

    if (!set.member_group_ids_by_currency[currencyId]) {
      set.member_group_ids_by_currency[currencyId] = [];
    }

    return set.member_group_ids_by_currency[currencyId];
  }

  /** Whether one FIELD of a cell failed the last check — drives the red outline in the grid. */
  isFieldInvalid(schedule: DailyCheckInSchedule, field: string): boolean {
    return !!schedule && !!schedule.invalidFields && schedule.invalidFields.indexOf(field) > -1;
  }

  /** Add or drop one field's outline, leaving the cell's other fields as they are. */
  private markField(schedule: DailyCheckInSchedule, field: string, invalid: boolean) {
    const fields = (schedule.invalidFields || []).filter(f => f !== field);

    schedule.invalidFields = invalid ? fields.concat(field) : fields;
  }

  /** The cell for this day in the currency currently being edited. */
  scheduleFor(set: DailyCheckInSet, day: number): DailyCheckInSchedule {
    return set.schedules.find(
      s => Number(s.day) === day && Number(s.settings_currency_id) === Number(this.selectedCurrencyId)
    );
  }

  /**
   * Blocks the decimal point while a scaled currency is on screen. IDR is entered in whole
   * thousands against a fixed ".000" tail, so "1.5" would read as "1.5.000" and means nothing.
   *
   * AppTwoDigitDecimal allows two decimals for every currency and is shared with another page,
   * so the extra restriction lives here rather than in it. A PASTE bypasses both handlers —
   * creditAmountProblem() is what still catches that.
   */
  onAmountKeydown(event: KeyboardEvent): boolean {
    if (this.amountScale(Number(this.selectedCurrencyId)) === 1) {
      return true;
    }

    return event.key !== '.' && event.key !== 'Decimal' && event.key !== ',';
  }

  onAmountChange(set: DailyCheckInSet, day: number, input: HTMLInputElement) {
    const schedule = this.scheduleFor(set, day);
    const amount = this.normaliseAmount(input.value);

    // Written back to the ELEMENT as well as the model. [value] is a one-way binding, so typing
    // "001" a second time would land the model on the same "1" it already held; seeing no
    // change, Angular would skip the DOM write and leave the stale "001" on screen.
    input.value = amount;
    schedule.amount = amount;
    this.markDirty(set);

    // (change) fires on blur, so an out-of-range amount is outlined the moment the operator
    // leaves the box rather than surviving all the way to Save. An empty box is left alone —
    // "not filled in yet" is the save check's business, not something to flag mid-edit.
    this.markField(schedule, 'amount', this.hasValue(amount) && !!this.creditAmountProblem(schedule));
  }

  /**
   * Tidy how an amount is written, without changing what it is worth.
   *
   * AppTwoDigitDecimal only vets keystrokes against a shape regex, so "001", ".5" and "1." are
   * all accepted, and a paste bypasses the directive entirely. The directive is shared with
   * another page, so the cleanup lives here rather than in it.
   *
   * Deliberately never rounds, clamps or drops a meaningful trailing zero: "0.10" stays "0.10",
   * and anything genuinely wrong ("abc", "-5", "0.05") is passed through untouched so
   * creditAmountProblem() flags it instead of it being silently rewritten into something the
   * operator never typed.
   */
  private normaliseAmount(value: string): string {
    const trimmed = `${value === null || value === undefined ? '' : value}`.trim();

    if (trimmed === '' || isNaN(Number(trimmed))) {
      return trimmed;
    }

    return trimmed
      .replace(/^\+/, '')             // "+5"    -> "5"
      .replace(/^(-?)\./, '$10.')     // ".5"    -> "0.5"
      .replace(/^(-?)0+(?=\d)/, '$1') // "001"   -> "1"
      .replace(/\.$/, '');            // "1."    -> "1"
  }

  /**
   * The range to show under a credit box holding an out-of-range amount, or '' when there is
   * nothing to say. Paired with the outline above: the border says "wrong", this says why.
   */
  amountRangeHint(schedule: DailyCheckInSchedule): string {
    if (Number(schedule.reward_type) !== DailyCheckInRewardType.Credit || !this.hasValue(schedule.amount)) {
      return '';
    }

    const bounds = this.creditBounds(schedule);
    const amount = Number(schedule.amount);

    if (!bounds || isNaN(amount) || amount >= bounds.min && amount <= bounds.max) {
      return '';
    }

    return `${this.translateService.instant('Min')} ${bounds.minText}`
      + ` · ${this.translateService.instant('Max')} ${bounds.maxText}`;
  }

  /**
   * Switching type clears the fields the other types own, mirroring what the API does inside
   * the save transaction — so the grid never shows a stale promo on a credit cell. Only the
   * edited currency's cell changes; the same day in other currencies is untouched.
   */
  onRewardTypeChange(set: DailyCheckInSet, day: number, rewardType: number) {
    const schedule = this.scheduleFor(set, day);

    // The type change clears the fields the other types own, so every outline on this cell goes.
    schedule.invalidFields = [];
    schedule.promotionIssueText = null;
    schedule.reward_type = rewardType === null || rewardType === undefined ? null : Number(rewardType);

    if (schedule.reward_type !== DailyCheckInRewardType.Credit) {
      schedule.amount = '';
    }

    if (schedule.reward_type !== DailyCheckInRewardType.Promo) {
      schedule.promotion_id = null;
      schedule.promotion_selection = [];
    }

    // The label lives in prize_name for both Prize and No Reward, so only clear it when the
    // cell is neither.
    if (schedule.reward_type !== DailyCheckInRewardType.Prize
      && schedule.reward_type !== DailyCheckInRewardType.None) {
      schedule.prize_name = null;
    }

    this.markDirty(set);
  }

  onPromotionSelect(set: DailyCheckInSet, day: number, item: any) {
    const schedule = this.scheduleFor(set, day);

    schedule.promotion_id = item ? item.id : null;
    // The picker only offers promotions that already cover the set, so this normally clears —
    // but deriving it keeps the outline honest if a stale option is ever chosen.
    this.applyPromotionIssue(set, schedule);
    this.markDirty(set);
  }

  onPromotionDeselect(set: DailyCheckInSet, day: number) {
    const schedule = this.scheduleFor(set, day);

    schedule.promotion_id = null;
    schedule.promotion_selection = [];
    this.markField(schedule, 'promotion_id', false);
    schedule.promotionIssueText = null;
    this.markDirty(set);
  }

  onScheduleFieldChange(set: DailyCheckInSet, day: number, field: string, value: any) {
    const schedule = this.scheduleFor(set, day);

    schedule[field] = value;
    this.markField(schedule, field, false);
    this.markDirty(set);
  }

  onCurrencyChange(currencyId: number) {
    // Every currency's cells are already in memory, so this is a pure rebind of the whole grid
    // — type, amount, promo, prize and image. No reload, and unsaved edits elsewhere survive.
    this.selectedCurrencyId = Number(currencyId);

    // Both are per-currency now: a group taken in THB is free in MYR, and the promo list is
    // filtered by this currency's groups. refreshUnassignedGroups() also refreshes promotions.
    this.refreshUnassignedGroups();
  }

  // ----- member groups --------------------------------------------------------------------

  /**
   * A promotion is offered when it runs in the currency being edited AND covers EVERY member
   * group the set serves there — one set pays one schedule to all its groups, so a promo
   * reaching only some of them would pay nothing to the rest. A promotion targeting no group at
   * all stays open to any set. Currency is the strict half: without a promotion_currencies row
   * there are no amounts for it to pay in that currency.
   */
  private refreshPromotionOptions() {
    const currencyId = Number(this.selectedCurrencyId);

    this.sets.forEach(set => {
      const groupIds = this.groupIdsFor(set).map(id => Number(id));

      set.promotionOptions = this.promotions.filter(promotion => {
        const currencies = (promotion.settings_currency_ids || []).map(id => Number(id));

        if (currencies.indexOf(currencyId) === -1) {
          return false;
        }

        const targets = (promotion.member_group_ids || []).map(id => Number(id));

        if (!targets.length) {
          return true; // targets no group in particular — open to any set
        }

        // A targeted promotion needs groups to match against, and must cover all of them.
        return groupIds.length > 0 && groupIds.every(id => targets.indexOf(id) > -1);
      });
    });
  }

  private refreshUnassignedGroups() {
    // Per currency: a group taken in THB is still free to use in MYR.
    const assigned = this.sets.reduce((acc, set) => acc.concat(this.groupIdsFor(set)), []);
    this.unassignedGroups = this.memberGroups.filter(g => assigned.indexOf(Number(g.id)) === -1);

    // The widget is handed this array directly, so every row needs one before it renders.
    this.sets.forEach(set => { set.addGroupSelection = set.addGroupSelection || []; });
    this.refreshPromotionOptions();
  }

  groupName(memberGroupId: number): string {
    const group = this.memberGroups.find(g => g.id === Number(memberGroupId));
    return group ? group.name : `#${memberGroupId}`;
  }

  /**
   * Ticks are applied when the panel closes, not on each click: assigning immediately would
   * shrink `unassignedGroups` mid-selection, and the new array identity would reset the
   * widget's search box under the operator's fingers.
   */
  onAddGroupOpen(set: DailyCheckInSet) {
    this.groupDropdownOpen = true;
    this.openGroupDropdownSet = set;
    set.addGroupSelection = [];
  }

  onAddGroupClose(set: DailyCheckInSet) {
    this.groupDropdownOpen = false;
    this.openGroupDropdownSet = null;

    (set.addGroupSelection || []).forEach(item => this.assignGroup(set, item.id));

    set.addGroupSelection = [];
    this.refreshUnassignedGroups();
    this.warnStrandedPromotions(set);
  }

  assignGroup(set: DailyCheckInSet, memberGroupId: any) {
    if (!memberGroupId) {
      return;
    }

    const id = Number(memberGroupId);

    const groupIds = this.groupIdsFor(set);

    if (groupIds.indexOf(id) === -1) {
      groupIds.push(id);
      // Clears as soon as the operator fixes it, rather than staying red until the next save.
      set.groupsInvalid = false;
      this.markDirty(set);
    }
  }

  removeGroup(set: DailyCheckInSet, memberGroupId: number) {
    const currencyId = Number(this.selectedCurrencyId);

    set.member_group_ids_by_currency[currencyId] =
      this.groupIdsFor(set).filter(id => id !== Number(memberGroupId));
    this.markDirty(set);
    this.refreshUnassignedGroups();

    // Removing usually widens coverage, but taking the set to zero groups strands any promotion
    // that targets specific ones — so the same check runs here.
    this.warnStrandedPromotions(set);
  }

  // ----- set actions ----------------------------------------------------------------------

  addSet() {
    const set: DailyCheckInSet = {
      id: null,
      name: `Set ${this.sets.length + 1}`,
      status: 1,
      member_group_ids_by_currency: {},
      schedules: this.blankGrid(),
    } as DailyCheckInSet;

    // No id yet, so isDirty() already reports it as pending — nothing to record. The pin keeps
    // the empty row on screen until it is configured.
    this.sets.push(set);
    this.pinnedSets.set(set, Number(this.selectedCurrencyId));
    this.refreshUnassignedGroups();
  }

  /** A blank cell for every (day, currency) — the API requires each currency to cover all 7. */
  private blankGrid(): DailyCheckInSchedule[] {
    const schedules: DailyCheckInSchedule[] = [];

    this.currencies.forEach(currency => {
      this.days.forEach(day => schedules.push(this.blankSchedule(day, Number(currency.id))));
    });

    return schedules;
  }

  /**
   * Copies only the currency being edited — the other currencies start unset, so duplicating
   * while on SGD never carries MYR's schedule along. Member groups aren't copied either: a
   * group can only live in one set.
   */
  duplicateSet(source: DailyCheckInSet) {
    const schedules: DailyCheckInSchedule[] = [];

    this.currencies.forEach(currency => {
      const currencyId = Number(currency.id);

      this.days.forEach(day => {
        if (currencyId !== Number(this.selectedCurrencyId)) {
          schedules.push(this.blankSchedule(day, currencyId));
          return;
        }

        const cell = source.schedules.find(
          sc => Number(sc.day) === day && Number(sc.settings_currency_id) === currencyId
        );

        schedules.push(cell
          ? { ...JSON.parse(JSON.stringify(cell)), id: undefined }
          : this.blankSchedule(day, currencyId));
      });
    });

    const copy: DailyCheckInSet = {
      id: null,
      name: `${source.name} (copy)`,
      status: source.status,
      member_group_ids_by_currency: {},
      schedules,
    } as DailyCheckInSet;

    this.sets.push(copy);
    this.pinnedSets.set(copy, Number(this.selectedCurrencyId));
    this.refreshUnassignedGroups();
  }

  /**
   * Scoped to the currency on screen: clearing SGD leaves MYR untouched. The whole set is only
   * deleted when the currency being cleared is the last one it has configured — otherwise
   * there would be no way to remove a set at all.
   */
  removeSet(set: DailyCheckInSet) {
    const configured = this.configuredCurrencyIds(set);
    const others = configured.filter(id => id !== Number(this.selectedCurrencyId));

    if (others.length) {
      this.confirm(
        `${this.translateService.instant('Clear the')} ${this.currencyCode(this.selectedCurrencyId)} `
          + this.translateService.instant('schedule from this set? Other currencies keep theirs.'),
        () => this.clearCurrency(set)
      );

      return;
    }

    // A set that was never saved has nothing to confirm — just drop it from the grid.
    if (!set.id) {
      this.sets = this.sets.filter(s => s !== set);
      this.pinnedSets.delete(set);
      this.refreshUnassignedGroups();
      return;
    }

    this.confirm(
      this.translateService.instant('Are you sure you want to remove this set? Its member groups will become unassigned.'),
      () => {
        this.scheduleLoading = true;
        const sub = this.dataService.deleteSet(set.id).subscribe(() => {
          this.sets = this.sets.filter(s => s.id !== set.id);
          this.dirtySets.delete(set);
          this.pinnedSets.delete(set);
          this.refreshUnassignedGroups();
          this.scheduleLoading = false;
        }, () => {
          // Spinner only — ApiErrorInterceptor already showed the dialog. See class header.
          this.scheduleLoading = false;
        });

        this.subscriptions.add(sub);
      }
    );
  }

  /** Resets every cell of the edited currency back to unset, leaving other currencies alone. */
  private clearCurrency(set: DailyCheckInSet) {
    set.schedules = set.schedules.map(schedule =>
      Number(schedule.settings_currency_id) === Number(this.selectedCurrencyId)
        ? this.blankSchedule(schedule.day, Number(schedule.settings_currency_id))
        : schedule);

    // The groups go with it. isCurrencyConfigured() reports a currency as configured when it
    // still holds groups, so leaving them behind keeps the "cleared" currency in the payload —
    // and every save then fails on 7 blank days that the operator can no longer reach.
    set.member_group_ids_by_currency[Number(this.selectedCurrencyId)] = [];

    // Unpin too, so the row drops out of this currency's grid instead of lingering empty.
    if (this.pinnedSets.get(set) === Number(this.selectedCurrencyId)) {
      this.pinnedSets.delete(set);
    }

    this.markDirty(set);
    this.refreshUnassignedGroups();
  }

  /** House confirm dialog — same shape as commission-releases and the other BO pages. */
  private confirm(text: string, onConfirm: () => void) {
    Swal.fire({
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translateService.instant('Yes'),
      reverseButtons: true,
    }).then(result => {
      if (result.value) {
        onConfirm();
      }
    });
  }

  toggleSetStatus(set: DailyCheckInSet) {
    set.status = set.status ? 0 : 1;
    this.markDirty(set);
  }

  onSetNameChange(set: DailyCheckInSet, name: string) {
    set.name = name;
    set.nameInvalid = false;
    this.markDirty(set);
  }

  /**
   * Recomputed after every edit, so putting a value back the way it was clears the flag —
   * partner-page gets the same behaviour from its JSON snapshot comparison.
   */
  private markDirty(set: DailyCheckInSet) {
    if (!set.id) {
      return; // never saved — pending until it has an id
    }

    if (JSON.stringify(this.toPayload(set)) === this.baselines.get(set)) {
      this.dirtySets.delete(set);
      return;
    }

    this.dirtySets.add(set);
  }

  /** Records a set's saved state as the baseline every later edit is compared against. */
  private snapshot(set: DailyCheckInSet) {
    this.baselines.set(set, JSON.stringify(this.toPayload(set)));
    this.dirtySets.delete(set);
  }

  isDirty(set: DailyCheckInSet): boolean {
    return !set.id || this.dirtySets.has(set);
  }

  get hasUnsavedSets(): boolean {
    return this.sets.some(set => this.isDirty(set));
  }

  /**
   * A row with no id has never reached the API, so Create alone is enough to fill it in — Edit
   * governs sets that already exist. This is what lets a create-only operator work on the set
   * they just added while every saved row around it stays read-only.
   */
  canEditSet(set: DailyCheckInSet): boolean {
    return this.canEditDailyCheckInSet || (this.canCreateDailyCheckInSet && !set.id);
  }

  /**
   * Save is only offered for work this operator may actually write: with Edit it is always
   * there, with Create alone it appears once there is a new set to create.
   */
  get canSaveSchedule(): boolean {
    return this.canEditDailyCheckInSet
      || (this.canCreateDailyCheckInSet && this.sets.some(set => !set.id));
  }

  /**
   * Which of the three picker states this set's promo cells are in. Each branch returns one of
   * the stable settings objects rather than building one — the widget re-initialises whenever
   * [settings] changes identity, which a fresh literal would trigger on every check.
   */
  promotionSettings(set: DailyCheckInSet) {
    if (!this.canEditSet(set)) {
      return this.promotionDropdownReadonlySettings;
    }

    return this.groupIdsFor(set).length
      ? this.promotionDropdownSettings
      : this.promotionDropdownDisabledSettings;
  }

  /**
   * The baseline is only taken once the API has answered (patchSettings), so this is what tells
   * an empty icon apart from one that simply has not loaded yet — without it the mandatory-field
   * cue flashes red on every page load.
   */
  get settingsLoaded(): boolean {
    return this.initSettingsValue !== '';
  }

  /** True once a toggle or the icon differs from the last-saved settings. */
  get settingsDirty(): boolean {
    return JSON.stringify(this.settingsForm.value) !== this.initSettingsValue;
  }

  /**
   * The floating icon is mandatory, so Save stays locked until one is attached. Also blocked
   * while an upload is in flight — otherwise the icon being replaced would be committed before
   * its replacement patches in.
   */
  get canSaveSettings(): boolean {
    return this.settingsDirty && !!this.settingsForm.value.icon_path && !this.iconUploading && !this.missedIconUploading;
  }

  get resetModeTooltip(): string {
    return [
      this.translateService.instant('Reset on Miss: missing a check-in resets the reward progress to the first reward.'),
      this.translateService.instant('Continue Progress: missing a day does not reset progress; the next check-in claims the next reward.'),
      this.translateService.instant('Daily Only: players can claim only the reward for the current day. Missed rewards cannot be claimed later.'),
    ].join('\n\n');
  }

  /**
   * The rule decides which reward every member gets next, so a change is confirmed first. The
   * <select> is put back until the operator says Yes; the form only changes on confirm.
   */
  onResetModeChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const next = Number(select.value);

    select.value = String(this.settingsForm.value.reset_on_missed);

    this.confirm(
      this.translateService.instant('Changing this rule will affect player check-in progress. Continue?'),
      () => this.settingsForm.patchValue({ reset_on_missed: next })
    );
  }

  // ----- uploads --------------------------------------------------------------------------

  onIconSelected(event: any) {
    this.iconUploading = true;
    this.upload(event, path => {
      this.settingsForm.patchValue({ icon_path: path });
    }, () => {
      this.iconUploading = false;
    });
  }

  onCellImageSelected(event: any, set: DailyCheckInSet, day: number) {
    this.upload(event, path => {
      this.onScheduleFieldChange(set, day, 'image_path', path);
    });
  }

  clearIcon() {
    this.settingsForm.patchValue({ icon_path: null });
  }

  onMissedIconSelected(event: any) {
    this.missedIconUploading = true;
    this.upload(event, path => {
      this.settingsForm.patchValue({ missed_icon_path: path });
    }, () => {
      this.missedIconUploading = false;
    });
  }

  clearMissedIcon() {
    this.settingsForm.patchValue({ missed_icon_path: null });
  }

  clearCellImage(set: DailyCheckInSet, day: number) {
    this.onScheduleFieldChange(set, day, 'image_path', null);
  }

  openIconPreview() {
    if (this.settingsForm.value.icon_path) {
      this.showIconPreview = true;
    }
  }

  closeIconPreview() {
    this.showIconPreview = false;
  }

  /**
   * /file is already on the interceptor's skippedPages whitelist, so the FormData survives
   * sanitizeObject(). Do not set Content-Type — Angular sets the multipart boundary.
   */
  private upload(event: any, onDone: (path: string) => void, onSettled?: () => void) {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      if (onSettled) {
        onSettled();
      }
      return;
    }

    const formData = new FormData();
    formData.append('files', file);
    formData.append('type', 'daily_checkin');

    this.loading = true;
    const sub = this.uploadHttpService.upload(formData).subscribe((res: any) => {
      if (res && res.length) {
        onDone(res[0]);
      } else {
        // HTTP 200 with no path: the error interceptor never fires, so without this the file
        // silently fails to attach and the operator believes it uploaded.
        Swal.fire(
          this.translateService.instant('Upload failed'),
          this.translateService.instant('The image did not upload. Please try again.'),
          'error'
        );
      }

      this.loading = false;
      event.target.value = '';

      if (onSettled) {
        onSettled();
      }
    }, () => {
      // Spinner only — ApiErrorInterceptor already showed the dialog. The success branch above
      // handles the one case it cannot see: HTTP 200 with an empty body.
      this.loading = false;
      event.target.value = '';

      if (onSettled) {
        onSettled();
      }
    });

    this.subscriptions.add(sub);
  }

  // ----- saving ---------------------------------------------------------------------------

  saveSettings() {
    // Guarded as well as disabled: the icon is mandatory, so a save is never sent without one
    // even if the button's disabled state is ever bypassed.
    if (!this.settingsForm.value.icon_path) {
      this.showValidationErrors([this.translateService.instant('Floating icon image is required')]);
      return;
    }

    this.settingsLoading = true;

    const sub = this.dataService.updateSettings(this.settingsForm.value).subscribe(res => {
      this.patchSettings(res);
      this.settingsLoading = false;
    }, () => {
      // Spinner only — ApiErrorInterceptor already showed the dialog. See class header.
      this.settingsLoading = false;
    });

    this.subscriptions.add(sub);
  }

  /**
   * One request per changed set. A set that fails validation leaves the others saved, which
   * is why the API is per-set rather than one bulk grid write.
   */
  saveSchedule() {
    const pending = this.sets.filter(set => this.isDirty(set) && this.canEditSet(set));

    if (!pending.length) {
      return;
    }

    // Checked here rather than left to the API: one incomplete cell would otherwise come back
    // as `schedules.34.amount is required`, which names no day and no currency.
    this.clearInvalidFlags();
    // clearInvalidFlags() wipes the load-time eligibility outlines too, so re-derive them before
    // validating — otherwise a stranded promo loses its reason the moment the operator saves.
    this.sets.forEach(set => this.flagPromotionEligibility(set));
    const problems = pending.reduce((all, set) => all.concat(this.validationErrors(set)), [] as string[]);

    if (problems.length) {
      this.showValidationErrors(problems);
      return;
    }

    this.scheduleLoading = true;

    // Sequential, not parallel: MemberGroupNotInAnotherCheckInSet checks the live pivot, so when a
    // group moves from one set to another the receiving set's PUT is rejected if it is validated
    // before the giving set's PUT has committed. Ordered so every set that releases a group goes
    // ahead of the set that claims it.
    const queue = this.orderForSave(pending);

    const saveNext = () => {
      const set = queue.shift();

      if (!set) {
        this.scheduleLoading = false;
        return;
      }

      const payload = this.toPayload(set);
      const request$ = set.id
        ? this.dataService.updateSet({ ...payload, id: set.id } as DailyCheckInSet)
        : this.dataService.add(payload as DailyCheckInSet);

      const sub = request$.subscribe((saved: DailyCheckInSet) => {
        const index = this.sets.indexOf(set);

        // The flag clears INSIDE the guard: dropping it first would mark an existing set clean
        // on a response that carried no row, unlocking the currency selector and hiding the
        // save hint while the grid still held stale data.
        // The response replaces the row, so the baseline is taken on the NEW object — a set
        // that was just created now has an id and stops reporting itself as pending.
        if (index > -1 && saved) {
          this.dirtySets.delete(set);
          this.sets[index] = this.hydrateSet(saved);
          this.snapshot(this.sets[index]);
          this.refreshUnassignedGroups();
        }

        saveNext();
      }, (error: any) => {
        // The dialog is still ApiErrorInterceptor's job — what happens here is the outlining it
        // cannot do: the response's `details` name the fields that failed, so the exact cells get
        // marked. The failed set keeps its dirty flag (snapshot() runs on success only).
        this.markServerRejectedFields(set, payload, error);
        saveNext();
      });

      this.subscriptions.add(sub);
    };

    saveNext();
  }

  /**
   * Orders sets so one that claims a (currency, group) pair saves after the set giving that pair
   * up. A swap between two sets is a cycle no order can satisfy; those keep their grid order and
   * the API reports the clash.
   */
  private orderForSave(sets: DailyCheckInSet[]): DailyCheckInSet[] {
    const pairsOf = (rows: any[]) => (rows || []).map(row => `${row.settings_currency_id}:${row.member_group_id}`);
    const info = sets.map(set => {
      const current = pairsOf(this.toPayload(set).member_groups);
      const baseline = this.baselines.get(set);
      const saved = set.id && baseline ? pairsOf(JSON.parse(baseline).member_groups) : [];

      return {
        set,
        claimed: current.filter(pair => saved.indexOf(pair) === -1),
        released: saved.filter(pair => current.indexOf(pair) === -1),
      };
    });

    const ordered: DailyCheckInSet[] = [];
    let remaining = info;

    while (remaining.length) {
      const ready = remaining.filter(item => !item.claimed.some(pair =>
        remaining.some(other => other !== item && other.released.indexOf(pair) > -1)
      ));
      const next = ready.length ? ready : [remaining[0]];

      next.forEach(item => ordered.push(item.set));
      remaining = remaining.filter(item => next.indexOf(item) === -1);
    }

    return ordered;
  }

  // ----- validation -----------------------------------------------------------------------

  /**
   * A currency counts as configured once ANY of its cells carries something the operator
   * entered. An untouched currency is dropped from the payload, which is what keeps a
   * single-currency brand from having to fill all 7 days x every active currency.
   */
  private isCurrencyConfigured(set: DailyCheckInSet, settingsCurrencyId: number): boolean {
    // Assigning a group is itself a start on that currency, even before any day is filled.
    if (this.groupIdsFor(set, settingsCurrencyId).length) {
      return true;
    }

    // Every cell now starts as No Reward, so the presence of a reward type says nothing about
    // whether the operator touched this currency. A currency counts as configured once a cell
    // moves OFF No Reward, or carries a label, image, amount or promotion.
    return set.schedules.some(schedule =>
      Number(schedule.settings_currency_id) === Number(settingsCurrencyId)
      && (Number(schedule.reward_type) !== DailyCheckInRewardType.None
        || this.hasValue(schedule.amount)
        || schedule.promotion_id !== null && schedule.promotion_id !== undefined
        || this.hasValue(schedule.prize_name)
        || this.hasValue(schedule.image_path))
    );
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && `${value}`.trim() !== '';
  }

  /** Currencies this set actually sends — see isCurrencyConfigured. */
  private configuredCurrencyIds(set: DailyCheckInSet): number[] {
    return this.currencies
      .map(currency => Number(currency.id))
      .filter(currencyId => this.isCurrencyConfigured(set, currencyId));
  }

  /** Public: the grid tooltips name the currency being edited. */
  currencyCode(settingsCurrencyId: number): string {
    const currency = this.currencies.find(c => Number(c.id) === Number(settingsCurrencyId));
    return currency ? currency.code : `#${settingsCurrencyId}`;
  }

  /**
   * Every problem that would make the API reject this set, phrased so the operator can find
   * the cell: set name, then one line per incomplete day naming its currency.
   */
  private validationErrors(set: DailyCheckInSet): string[] {
    const errors: string[] = [];
    const label = this.hasValue(set.name) ? set.name : this.translateService.instant('(unnamed set)');

    if (!this.hasValue(set.name)) {
      set.nameInvalid = true;
      errors.push(this.translateService.instant('One set has no name yet — give it a name.'));
    }

    const currencyIds = this.configuredCurrencyIds(set);

    if (!currencyIds.length) {
      errors.push(`${this.translateService.instant('Set')} "${label}" — `
        + this.translateService.instant('nothing is set up yet. Add a member group, or set a day to something other than No Reward.'));
      return errors;
    }

    currencyIds.forEach(currencyId => {
      // A currency the set pays in must say WHO it pays. Copying a set is how this slips
      // through: the copy brings the schedule but never the groups, because a group can only
      // belong to one set per currency. Mirrors CheckInMemberGroupGrid on the API side.
      if (!this.groupIdsFor(set, currencyId).length) {
        set.groupsInvalid = true;
        errors.push(`${this.translateService.instant('Set')} "${label}" · ${this.currencyCode(currencyId)} — `
          + this.translateService.instant('add at least one member group'));
      }

      this.days.forEach(day => {
        const schedule = set.schedules.find(
          s => Number(s.day) === day && Number(s.settings_currency_id) === currencyId
        );

        const missing = schedule
          ? this.missingLabels(set, schedule)
          : [{ field: 'reward_type', message: this.translateService.instant('choose a reward type') }];

        if (!missing.length) {
          return;
        }

        // Outlines only the boxes at fault, so after dismissing the list the operator can see
        // WHICH field in the cell needs attention rather than a wall of red.
        if (schedule) {
          missing.forEach(problem => this.markField(schedule, problem.field, true));
        }

        // Each problem is a complete instruction on its own, so they simply join with commas —
        // a shared "still needs" prefix could not fit both "add an image" and a statement like
        // "X is not available to WINNER" without one of them reading as broken English.
        errors.push(`${this.translateService.instant('Set')} "${label}" · ${this.currencyCode(currencyId)} · `
          + `${this.translateService.instant('Day')} ${day} — `
          + `${missing.map(problem => problem.message).join(', ')}`);
      });
    });

    return errors;
  }


  /**
   * What a cell is still missing, in words. "incomplete on Day 3" left the operator hunting;
   * naming the field means the dialog and the red outline say the same thing.
   */
  /**
   * Whether a promo cell's chosen promotion still qualifies: it must run in THAT CELL'S currency
   * and reach a group the set holds there. Computed from `promotions` rather than
   * `set.promotionOptions`, because those options are built for the currency on screen only —
   * validating another currency's cell against them would fail it wrongly. Mirrors the server's
   * CheckInPromotionAvailable, so the two agree.
   */
  private promotionProblem(set: DailyCheckInSet, schedule: DailyCheckInSchedule): string {
    const issue = this.promotionIssue(set, schedule);

    return issue ? issue.lead + issue.groups : '';
  }

  /**
   * Why a promo cell's promotion cannot run, split so the caller can render it: `lead` is the
   * sentence, `groups` the member-group names that finish it (empty when the reason is not
   * about groups). Kept apart only so the dialog can embolden the names without a fragile
   * search-and-replace back through the finished sentence.
   *
   * Returns null when the cell is fine. Mirrors CheckInPromotionAvailable server-side.
   */
  private promotionIssue(set: DailyCheckInSet, schedule: DailyCheckInSchedule): { lead: string, groups: string } {
    const promotion = this.promotions.find(p => Number(p.id) === Number(schedule.promotion_id));

    if (!promotion) {
      return { lead: this.translateService.instant('The chosen promotion is no longer active.'), groups: '' };
    }

    const currencyId = Number(schedule.settings_currency_id);
    const currencies = (promotion.settings_currency_ids || []).map(id => Number(id));

    if (currencies.indexOf(currencyId) === -1) {
      return {
        lead: `${this.translateService.instant('Promotion')} "${promotion.name}" `
          + `${this.translateService.instant('does not run in')} ${this.currencyCode(currencyId)}.`,
        groups: '',
      };
    }

    const targets = (promotion.member_group_ids || []).map(id => Number(id));

    if (!targets.length) {
      return null; // targets no group in particular — open to any set
    }

    const groupIds = this.groupIdsFor(set, currencyId).map(id => Number(id));

    if (!groupIds.length) {
      return {
        lead: `${this.translateService.instant('Promotion')} "${promotion.name}" `
          + `${this.translateService.instant('is only for selected member groups, but this set has none in')} `
          + `${this.currencyCode(currencyId)}.`,
        groups: '',
      };
    }

    // Every group must be covered, not just one — see refreshPromotionOptions().
    const uncovered = groupIds.filter(id => targets.indexOf(id) === -1);

    if (!uncovered.length) {
      return null;
    }

    return {
      lead: `${this.translateService.instant('Promotion')} "${promotion.name}" `
        + `${this.translateService.instant('is not available to member group')}: `,
      groups: uncovered.map(id => this.groupName(id)).join(', '),
    };
  }

  /**
   * Outline whatever the API rejected.
   *
   * The 422 carries `details` keyed by the payload path that failed — `schedules.34.promotion_id`,
   * `member_groups.2.member_group_id`, `name`. Index 34 is a position in the array THIS save sent,
   * so the payload is read back to turn it into a (day, currency) and find the cell on screen.
   *
   * Needed because the two sides can disagree: the client checks a promotion against the
   * promotion list it loaded, while CheckInPromotionAvailable re-reads promotion_member_groups at
   * save time. When they differ the operator got a message naming the promotion but nothing
   * showing WHICH of the 7 x currencies cells held it.
   *
   * Defensive throughout — a differently-shaped error must not break the save's own error path.
   */
  private markServerRejectedFields(set: DailyCheckInSet, payload: any, error: any) {
    const details = error && error.details;

    if (!details || typeof details !== 'object') {
      return;
    }

    let rejectedCurrencyId: number = null;

    Object.keys(details).forEach(path => {
      const parts = path.split('.');

      if (parts[0] === 'name') {
        set.nameInvalid = true;
        return;
      }

      if (parts[0] === 'member_groups') {
        set.groupsInvalid = true;
        return;
      }

      if (parts[0] !== 'schedules' || parts.length < 3) {
        return;
      }

      const sent = (payload.schedules || [])[Number(parts[1])];

      if (!sent) {
        return;
      }

      const schedule = set.schedules.find(row => Number(row.day) === Number(sent.day)
        && Number(row.settings_currency_id) === Number(sent.settings_currency_id));

      if (!schedule) {
        return;
      }

      this.markField(schedule, parts[2], true);

      if (rejectedCurrencyId === null) {
        rejectedCurrencyId = Number(schedule.settings_currency_id);
      }

      // Show the server's own wording in the cell. It is the authority here — it re-read the
      // promotion's groups at save time, which is exactly what the client copy got wrong.
      if (parts[2] === 'promotion_id') {
        const messages = details[path];
        schedule.promotionIssueText = Array.isArray(messages) ? messages.join(' ') : `${messages}`;
      }
    });

    // A rejected cell in another currency would be outlined where nobody can see it — and the
    // selector is locked while the set is dirty, which a failed save leaves it, so the operator
    // could not switch to it either. Move the grid there instead. Only within the operator's own
    // currencies: scoping still wins over a server message about one they cannot open.
    if (rejectedCurrencyId !== null
      && rejectedCurrencyId !== Number(this.selectedCurrencyId)
      && this.editableCurrencies.some(currency => Number(currency.id) === rejectedCurrencyId)) {
      this.onCurrencyChange(rejectedCurrencyId);
    }
  }

  /**
   * Outline the promo picker and say why, judged against the cell's OWN currency. Assigned from
   * the current answer rather than only switched on, so fixing the cause clears it.
   */
  private applyPromotionIssue(set: DailyCheckInSet, schedule: DailyCheckInSchedule) {
    const issue = this.promotionIssue(set, schedule);

    schedule.promotionIssueText = issue ? issue.lead + issue.groups : null;
    this.markField(schedule, 'promotion_id', !!issue);
  }

  /**
   * A promo cell goes stale while nobody is looking: the promotion's own member groups or
   * currencies change server-side, or the set gained a group in an earlier session. Nothing
   * re-checked them on the way in, so the grid opened looking clean and only turned red once
   * the operator touched the cell or hit Save. Swept across EVERY currency, not just the one on
   * screen — a cell the operator cannot see is just as unsavable.
   */
  private flagPromotionEligibility(set: DailyCheckInSet) {
    set.schedules
      .filter(schedule => Number(schedule.reward_type) === DailyCheckInRewardType.Promo
        && schedule.promotion_id !== null && schedule.promotion_id !== undefined)
      .forEach(schedule => this.applyPromotionIssue(set, schedule));
  }

  /**
   * Adding a group narrows what a promo day can use: a promotion has to cover EVERY group in the
   * set, so a group it does not target strands days that were valid a moment ago.
   *
   * Reported at the click that caused it rather than only at save — by then the operator has
   * moved on, and a rejection naming just the day hides which group made it fail.
   */
  private warnStrandedPromotions(set: DailyCheckInSet) {
    const currencyId = Number(this.selectedCurrencyId);

    const checked = set.schedules
      .filter(schedule => Number(schedule.settings_currency_id) === currencyId
        && Number(schedule.reward_type) === DailyCheckInRewardType.Promo
        && schedule.promotion_id !== null && schedule.promotion_id !== undefined)
      .map(schedule => ({ schedule, issue: this.promotionIssue(set, schedule) }));

    // Assigned from the current answer, not just switched on: a group change can un-strand a
    // promo day as easily as strand one, and an outline that only ever turns on leaves a cell
    // marked wrong after the operator has already fixed it by removing the group.
    checked.forEach(row => {
      row.schedule.promotionIssueText = row.issue ? row.issue.lead + row.issue.groups : null;
      this.markField(row.schedule, 'promotion_id', !!row.issue);
    });

    const stranded = checked.filter(row => row.issue);

    if (!stranded.length) {
      return;
    }

    // One line per DISTINCT reason, not per day: the same promotion sitting on four days is one
    // thing to fix, and repeating it four times was most of what made this dialog a wall of text.
    const seen: string[] = [];
    const rows = stranded
      .map(row => row.issue)
      .filter(issue => {
        const key = issue.lead + issue.groups;

        if (seen.indexOf(key) > -1) {
          return false;
        }

        seen.push(key);

        return true;
      })
      .map(issue => '<p class="mb-2">' + this.escapeHtml(issue.lead)
        + (issue.groups ? '<strong>' + this.escapeHtml(issue.groups) + '</strong>' : '') + '</p>')
      .join('');

    // Bulleted, and using the same &bull; markup as the save dialog, so the two ways out read as
    // two choices rather than one run-on sentence.
    const actions = [
      this.translateService.instant('Choose another promotion'),
      this.translateService.instant('Or remove the member group'),
    ]
      .map(action => '<div style="margin-bottom: 4px;">'
        + '<span style="color: #48465b; margin-right: 8px;">&bull;</span>' + action + '</div>')
      .join('');

    Swal.fire({
      title: '<div class="text-center">' + this.translateService.instant('Promotion Not Available') + '</div>',
      html: '<div class="text-left" style="padding-left: 0.5rem;">'
        + rows
        + '<div class="mt-3">' + actions + '</div>'
        + '</div>',
      icon: 'warning',
      showConfirmButton: false,
      showDenyButton: true,
      showCloseButton: true,
      denyButtonText: this.translateService.instant('Close'),
      customClass: {
        denyButton: 'deny-button',
      }
    });
  }

  private missingLabels(set: DailyCheckInSet, schedule: DailyCheckInSchedule): { field: string, message: string }[] {
    const missing: { field: string, message: string }[] = [];

    if (!this.hasValue(schedule.reward_type)) {
      return [{ field: 'reward_type', message: this.translateService.instant('choose a reward type') }];
    }

    // A day that pays nothing needs nothing: both its label and its image are optional.
    if (Number(schedule.reward_type) === DailyCheckInRewardType.None) {
      return [];
    }

    // Every paying cell shows the player an image, so a missing one outlines the drop-zone.
    if (!this.hasValue(schedule.image_path)) {
      missing.push({ field: 'image_path', message: this.translateService.instant('add an image') });
    }

    if (Number(schedule.reward_type) === DailyCheckInRewardType.Promo) {
      if (schedule.promotion_id === null || schedule.promotion_id === undefined) {
        missing.push({ field: 'promotion_id', message: this.translateService.instant('choose a promotion') });
      } else {
        // Changing a set's member groups can strand a promo chosen earlier. Caught here so the
        // operator gets the day, the groups and the box outlined, not a bare API rejection.
        const problem = this.promotionProblem(set, schedule);

        if (problem) {
          missing.push({ field: 'promotion_id', message: problem });
        }
      }
    }

    if (Number(schedule.reward_type) === DailyCheckInRewardType.Prize && !this.hasValue(schedule.prize_name)) {
      missing.push({ field: 'prize_name', message: this.translateService.instant('enter a prize name') });
    }

    if (Number(schedule.reward_type) === DailyCheckInRewardType.Credit) {
      const problem = this.creditAmountProblem(schedule);

      if (problem) {
        missing.push({ field: 'amount', message: problem });
      }
    }

    return missing;
  }

  /**
   * Why a credit cell's amount is unacceptable, or '' when it is fine.
   *
   * The bounds are the ones the API sent with each currency (CheckInCreditAmountRange), so the
   * rule keeps a single definition server-side and this only reports it — IDR's floor is 1000,
   * not 0.10, and hard-coding either here would drift. Checked against the CELL's currency
   * rather than the one on screen: a set holds cells for every currency it is configured in.
   *
   * Without this the box only had to be non-empty, so 0.05 in MYR or 500 in IDR passed the
   * pre-save check and came back as a bare 422 that named no day.
   */
  private creditAmountProblem(schedule: DailyCheckInSchedule): string {
    const amount = Number(schedule.amount);

    if (!this.hasValue(schedule.amount) || isNaN(amount)) {
      return this.translateService.instant('enter an amount');
    }

    const bounds = this.creditBounds(schedule);

    // No bounds from the API leaves nothing to compare against, so fall back to the one thing
    // that holds in every currency: a credit day has to pay something.
    if (!bounds) {
      return amount > 0 ? '' : this.translateService.instant('enter an amount');
    }

    if (amount < bounds.min || amount > bounds.max) {
      return `${this.translateService.instant('enter an amount from')} ${bounds.minText} `
        + `${this.translateService.instant('to')} ${bounds.maxText}`;
    }

    // A scaled box already shows its own fraction as a fixed suffix, so "1.5" would read as
    // "1.5.000". Whole units only — which is also what keeps toStoredAmount() exact.
    if (this.amountScale(Number(schedule.settings_currency_id)) > 1 && amount % 1 !== 0) {
      return this.translateService.instant('enter a whole number');
    }

    return '';
  }

  /**
   * The credit bounds the API sent for a cell's own currency, or null when it sent none.
   *
   * One lookup shared by the blur hint and the save check, so the border, the hint and the
   * rejection can never disagree about what counts as in range.
   */
  private creditBounds(schedule: DailyCheckInSchedule): { min: number, max: number, minText: string, maxText: string } {
    const currency = this.currencies.find(c => Number(c.id) === Number(schedule.settings_currency_id));

    if (!currency || !currency.min_amount || !currency.max_amount) {
      return null;
    }

    // Converted into box units, so every comparison and every message below is in the same
    // units the operator is typing — IDR reads "Min 1 · Max 999", not "Min 1000 · Max 999000".
    const minText = this.toDisplayAmount(currency.min_amount, Number(currency.id));
    const maxText = this.toDisplayAmount(currency.max_amount, Number(currency.id));
    const min = Number(minText);
    const max = Number(maxText);

    if (!min || !max || isNaN(min) || isNaN(max)) {
      return null;
    }

    return { min, max, minText, maxText };
  }

  /**
   * SweetAlert renders `html` as markup, and these lines carry operator-entered text — a set
   * named `<img src=x onerror=…>` would otherwise execute in every operator's browser. Escaped
   * at the sink rather than at each source, so a new message can't reintroduce the hole.
   */
  private escapeHtml(value: string): string {
    return `${value}`
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Drops every outline — called before each save attempt so old ones never linger. */
  private clearInvalidFlags() {
    this.sets.forEach(set => {
      set.nameInvalid = false;
      set.groupsInvalid = false;
      set.schedules.forEach(schedule => {
        schedule.invalidFields = [];
        schedule.promotionIssueText = null;
      });
    });
  }

  /**
   * Same shape as the leave-page prompt: warning icon, point-form list, one red button. It is a
   * deny button rather than a confirm one — that is where SweetAlert's red comes from.
   */
  private showValidationErrors(problems: string[]) {
    const shown = problems.slice(0, DailyCheckInComponent.MAX_LISTED_PROBLEMS);
    const rest = problems.length - shown.length;

    const rows = shown
      .map(problem => '<div style="margin-bottom: 4px;"><span style="color: #48465b; margin-right: 8px;">&bull;</span>'
        + this.escapeHtml(problem) + '</div>')
      .join('');

    const more = rest > 0
      ? `<div class="mt-2">...${this.translateService.instant('and')} ${rest} ${this.translateService.instant('more')}</div>`
      : '';

    Swal.fire({
      title: '<div class="text-center">' + this.translateService.instant('Unsaved Changes') + '</div>',
      html: '<div class="text-left" style="padding-left: 0.5rem;">'
        + '<p class="mb-2">' + this.translateService.instant('These changes cannot be saved yet:') + '</p>'
        + rows
        + more
        + '</div>',
      icon: 'warning',
      showConfirmButton: false,
      showDenyButton: true,
      showCloseButton: true,
      denyButtonText: this.translateService.instant('Close'),
      customClass: {
        denyButton: 'deny-button',
      }
    });
  }

  private toPayload(set: DailyCheckInSet): Partial<DailyCheckInSet> {
    const currencyIds = this.configuredCurrencyIds(set);

    return {
      name: set.name,
      status: set.status,
      // One row per (currency, group), and only for currencies this save carries — a currency
      // left out keeps neither cells nor groups.
      member_groups: currencyIds.reduce((rows, currencyId) => rows.concat(
        this.groupIdsFor(set, currencyId).map(groupId => ({
          settings_currency_id: currencyId,
          member_group_id: groupId,
        }))
      ), []),
      // Every configured currency goes up on every save, not just the one on screen — the API
      // replaces a set's cells wholesale, so a partial payload would delete the rest. A
      // currency the operator never touched is omitted so it isn't forced to be filled in.
      schedules: set.schedules
        .filter(schedule => currencyIds.indexOf(Number(schedule.settings_currency_id)) > -1)
        .map(schedule => {
          const base: any = {
            day: schedule.day,
            settings_currency_id: schedule.settings_currency_id,
            reward_type: schedule.reward_type,
            image_path: schedule.image_path || null,
          };

          if (schedule.reward_type === DailyCheckInRewardType.Credit) {
            base.amount = this.toStoredAmount(schedule.amount, Number(schedule.settings_currency_id));
          }

          if (schedule.reward_type === DailyCheckInRewardType.Promo) {
            base.promotion_id = schedule.promotion_id;
          }

          // No Reward shares prize_name for its optional label — the API stores both there and
          // uses reward_type as the discriminator. Omitting it here dropped the label on save.
          if (schedule.reward_type === DailyCheckInRewardType.Prize
            || schedule.reward_type === DailyCheckInRewardType.None) {
            base.prize_name = schedule.prize_name || null;
          }

          return base;
        }),
    };
  }
}
